import { Node, mergeAttributes, nodePasteRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import SmartLinkComponent from './SmartLinkComponent';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    smartLink: {
      /** Insert a Smart Link node at the current cursor position */
      setSmartLink: (attributes: { href: string }) => ReturnType;
    };
  }
}

export const SmartLink = Node.create({
  name: 'smartLink',
  priority: 10000,

  group: 'inline',

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (element) => element.getAttribute('href'),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-smart-title'),
      },
      icon: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-smart-icon'),
      },
      isInternal: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-smart-internal'),
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-smart-link]',
        getAttrs: (node) => ({
          href: (node as HTMLElement).getAttribute('data-smart-link'),
          title: (node as HTMLElement).getAttribute('data-smart-title'),
          icon: (node as HTMLElement).getAttribute('data-smart-icon'),
          isInternal: (node as HTMLElement).getAttribute('data-smart-internal'),
        }),
      },
      {
        tag: 'span[data-smart-link]',
        getAttrs: (node) => ({
          href: (node as HTMLElement).getAttribute('data-smart-link'),
        }),
      },
      // Fallback for old <a> tags
      {
        tag: 'a[href]',
        priority: 10001,
        getAttrs: (node) => ({
          href: (node as HTMLElement).getAttribute('href'),
        }),
      },
    ];
  },
  
  renderHTML({ node, HTMLAttributes }) {
    const title = node.attrs.title || node.attrs.href || '';
    const icon = node.attrs.icon;
    
    // Tiptap render structure: [tag, attrs, child1, child2...]
    const children: any[] = [];
    if (icon) {
      children.push(['img', { src: icon, alt: '', class: 'w-3 h-3 object-contain mr-1 inline-block rounded-sm pointer-events-none align-baseline' }]);
    }
    children.push(['span', { class: 'truncate flex-1 min-w-0' }, title]);

    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        href: node.attrs.href || '',
        target: '_blank',
        rel: 'noopener noreferrer',
        'data-smart-link': node.attrs.href || '',
        'data-smart-title': node.attrs.title || '',
        'data-smart-icon': node.attrs.icon || '',
        'data-smart-internal': node.attrs.isInternal || '',
        class: 'smart-link-node',
      }),
      ...children
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SmartLinkComponent);
  },

  addCommands() {
    return {
      setSmartLink:
        (attributes: { href: string }) =>
        ({ chain }: any) => {
          return chain()
            .insertContent({ type: this.name, attrs: attributes })
            .run();
        },
    };
  },

  /**
   * Paste rule: when a bare URL is pasted, convert it into a SmartLink node.
   * This fires on paste only, not on regular typing.
   */
  addPasteRules() {
    return [
      nodePasteRule({
        find: /https?:\/\/[^\s$.?#].[^\s]*/g,
        type: this.type,
        getAttributes: (match) => ({
          href: match[0],
        }),
      }),
    ];
  },
});
